import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * useColumnWidths — user-draggable column widths for one table surface,
 * persisted to `localStorage`.
 *
 * Product owner, 2026-08-09: *"maybe make the columns manual adjustmennt by the
 * user?"*, and, offered three scopes, **one set of widths per user shared
 * across every department**.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔑 ONE WIDTH MODEL. THAT IS THE WHOLE REASON THIS IS A HOOK.
 * ═══════════════════════════════════════════════════════════════════════════
 * The Gag Breakdown renders **N separate `<table>` elements, one per group**,
 * inside ONE horizontal scroll region and forced to a common width so the group
 * cards line up. A drag must therefore resize that column in **every group
 * simultaneously**. This hook holds a single `widths` map; every group's
 * `<colgroup>` is built from that same map, so the groups **cannot** diverge —
 * there is no per-group state for them to diverge with.
 *
 * 🚨 **PER-GROUP WIDTH STATE IS THE SINGLE MOST LIKELY WAY TO GET THIS WRONG,
 * AND IT LOOKS CORRECT IN A ONE-GROUP DEPARTMENT.** It breaks the moment a
 * department has two groups, which is the ordinary case. If you ever find
 * yourself calling this hook inside a per-group render, stop.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🚨 THE STORED SHAPE IS A MAP KEYED BY COLUMN KEY, NEVER AN ARRAY BY INDEX
 * ═══════════════════════════════════════════════════════════════════════════
 *   ✅ { "title": 150, "status": 150, "shoot": 160 }
 *   ❌ [150, 150, 50, 140, …]
 *
 * An index array silently mis-assigns every width after a column is added,
 * removed, reordered or hidden — and the Breakdown has ALREADY had a column
 * hidden (`Effect` / `primary_effect_type`). Keying by `key` is what lets
 * `sanitise` below drop what it does not recognise and default what is missing,
 * which is what makes the format **self-healing and therefore version-free**.
 * A stored layout from before a column existed keeps working; so does one from
 * after a column was removed.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🪤 EVERY REJECTED VALUE IS REJECTED BY `Number.isFinite`, NOT BY `>=` / `<=`
 * ═══════════════════════════════════════════════════════════════════════════
 * `localStorage` is user-writable and `JSON.parse` will hand back anything.
 * `Number.isFinite` rejects `NaN`, `Infinity`, `null`, `undefined`, `true` —
 * **and every string**, which is the case a bare range check gets wrong:
 * `'120' >= 70 && '120' <= 600` is **`true`**, so a comparison-only guard
 * accepts the STRING, stores it, and `style={{ width: "120" }}` then works by
 * accident until something does arithmetic on it and concatenates. That is
 * CLAUDE.md's *"the fixture's TYPE is part of the assertion"* rule; the test
 * file carries `'{"a":"120"}'` as a case for exactly this reason.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🪤 THE STORAGE KEY IS PASSED IN AS A CONSTANT, SO `check-bloat` CANNOT SEE IT
 * ═══════════════════════════════════════════════════════════════════════════
 * Its orphan-key scan is a STRING-LITERAL regex (`scripts/check-bloat.js:230`),
 * so a key passed as a variable is invisible in **both** directions: never
 * reported as an orphan, never reported at all. **Grep the key name by hand**
 * before concluding it is unused. 41 `localStorage.setItem(CONST, …)` sites
 * under `client/src` already share this blind spot.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 IT KNOWS NOTHING ABOUT GAGS, AND THAT IS DELIBERATE — BUT IT IS NOT AN
 * INVITATION TO ROLL IT OUT
 * ═══════════════════════════════════════════════════════════════════════════
 * The product owner scoped drag-to-resize to the Breakdown alone: *"we will
 * likley apply this to the department orders and to approve tables but not
 * yet."* Keeping the hook generic costs nothing and means a future adoption is
 * an import rather than an extraction. **"Do not foreclose it" is not "build
 * for it"** — do not add options here for a caller that does not exist.
 *
 * @param {Array<{key: string, width: number, min: number}>} columns
 *        The column model. `width` is the DEFAULT (what a reset returns to);
 *        `min` is that column's floor. A column with no `min` gets `0`.
 * @param {string} storageKey  the `sfx_`-prefixed `localStorage` key.
 * @param {number} maxWidth    the single ceiling every column shares.
 * @returns {{
 *   widths: Record<string, number>,
 *   setWidth: (key: string, px: number) => void,
 *   nudgeWidth: (key: string, deltaPx: number) => void,
 *   resetColumn: (key: string) => void,
 *   resetAll: () => void,
 *   totalWidth: number,
 *   isCustomised: boolean,
 * }}
 */
export default function useColumnWidths(columns, storageKey, maxWidth) {
  const defaults = useMemo(
    () => Object.fromEntries(columns.map((c) => [c.key, c.width])),
    [columns]
  );

  /**
   * Coerce whatever came out of storage into a complete, valid width map.
   *
   * 🔑 It builds a FRESH object from `columns` and copies nothing it was not
   * asked for, so an unknown key cannot survive by being spread through. That
   * is the same allowlist-by-construction device `server/helpers/orderPreview.js`
   * uses, and for the same reason: an absence is invisible in a diff, so it has
   * to be structural rather than remembered.
   */
  const sanitise = useCallback((raw) => {
    const out = {};
    for (const c of columns) {
      const v = raw == null ? undefined : raw[c.key];
      const min = Number.isFinite(c.min) ? c.min : 0;
      out[c.key] = Number.isFinite(v) && v >= min && v <= maxWidth ? v : c.width;
    }
    return out;
  }, [columns, maxWidth]);

  const [widths, setWidths] = useState(() => {
    try {
      // 🪤 `JSON.parse(null)` is `null`, not a throw — `sanitise` handles it.
      return sanitise(JSON.parse(localStorage.getItem(storageKey)));
    } catch {
      // Corrupt JSON is not an error the user caused and not one they can act
      // on. Fall back silently to the measured defaults.
      return defaults;
    }
  });

  /**
   * 🪤 **THE FIRST WRITE MUST NOT HAPPEN, and skipping it is not a
   * micro-optimisation.** Without the guard, merely VISITING the Breakdown
   * stamps the defaults into `localStorage` — after which the key exists for
   * every user who has never touched a handle, `isCustomised` is meaningless as
   * a signal, and a later change to a measured default is silently overridden
   * for everyone by a value they never chose. The key must appear only when a
   * user actually resizes something.
   */
  const hydrated = useRef(false);
  useEffect(() => {
    if (!hydrated.current) { hydrated.current = true; return; }
    try {
      localStorage.setItem(storageKey, JSON.stringify(widths));
    } catch {
      // Quota / private mode. A lost preference is not worth an exception that
      // unmounts the table the user is looking at.
    }
  }, [widths, storageKey]);

  const clamp = useCallback((col, px) => {
    const min = Number.isFinite(col.min) ? col.min : 0;
    return Math.max(min, Math.min(maxWidth, Math.round(px)));
  }, [maxWidth]);

  const setWidth = useCallback((key, px) => {
    const col = columns.find((c) => c.key === key);
    // 🪤 A non-finite `px` is DROPPED, never clamped. `Math.round(NaN)` is
    // `NaN` and `Math.max(70, NaN)` is `NaN`, so a clamp alone would happily
    // store `NaN` and blank every column in every group at once.
    if (!col || !Number.isFinite(px)) return;
    setWidths((w) => {
      const next = clamp(col, px);
      return next === w[key] ? w : { ...w, [key]: next };
    });
  }, [columns, clamp]);

  /**
   * Move one column by a delta — the keyboard path.
   *
   * ⌨️ **Deliberately expressed against the STORED width, not against the
   * rendered one.** A pointer drag has to track the pointer, so it works in
   * rendered pixels and undoes the table's stretch-to-fit (see
   * `GagGroupTable.js`). A key press has no pointer to track: `ArrowRight` means
   * "make this column ten more", which is the number `aria-valuenow` reports and
   * the number that gets stored. Making the two identical would mean a key press
   * changing `aria-valuenow` by an amount that depends on the window width.
   */
  const nudgeWidth = useCallback((key, delta) => {
    const col = columns.find((c) => c.key === key);
    if (!col || !Number.isFinite(delta)) return;
    setWidths((w) => {
      const next = clamp(col, w[key] + delta);
      return next === w[key] ? w : { ...w, [key]: next };
    });
  }, [columns, clamp]);

  const resetColumn = useCallback((key) => {
    if (!(key in defaults)) return;
    setWidths((w) => (w[key] === defaults[key] ? w : { ...w, [key]: defaults[key] }));
  }, [defaults]);

  const resetAll = useCallback(() => setWidths(defaults), [defaults]);

  const totalWidth = useMemo(
    () => columns.reduce((sum, c) => sum + widths[c.key], 0),
    [columns, widths]
  );

  // 🔻 **No live caller as of 2026-09-28** — every table that once drove a
  // "Reset column widths" menu item's disabled state from this now offers
  // `Fit columns to screen` instead, which asks a different question
  // (`hooks/useFitColumnsToScreen.js`'s own `alreadyFit`, not "is this
  // still at its shipped default"). Kept: it is a genuine, independently
  // tested capability of this generic hook (`useColumnWidths.test.js`) and
  // a plain reset-to-default is a real thing a future caller may still
  // want — "do not foreclose it" per this file's own docblock.
  const isCustomised = useMemo(
    () => columns.some((c) => widths[c.key] !== defaults[c.key]),
    [columns, widths, defaults]
  );

  return { widths, setWidth, nudgeWidth, resetColumn, resetAll, totalWidth, isCustomised };
}

// ── ARCHAEOLOGY (moved from root CLAUDE.md 2026-09-25) ──────────────────────
// The root `CLAUDE.md` Shared Modules row for this hook and
// `components/ui/ColumnResizeHandle.js` keeps every RULE (min on every column,
// Number.isFinite not comparisons, no write on hydration, the strip stays
// inside its cell, the grip not a line, opacity for hover, the mark is not the
// hit area) and points here. The measurements behind those rules, moved
// VERBATIM from that row:
//
// On the strip that shipped straddling the seam: — measured with
// `elementFromPoint`, the outer 5px of a 9px strip returned the following
// column's sort button and a pointer aimed at the middle did nothing at all.
//
// 🎨 **THE HANDLE IS VISIBLE AT REST SINCE 2026-08-09, AND THE ENTRY THAT SAID
// IT WAS THE "GHOST WEIGHT" IS SUPERSEDED.** Product owner, testing increment 2:
// *"I think we should have a very light colour for the dragable handles for the
// table column headers. currently we have to hover to try and find them"*.
//
// The caret's faint 1.80/2.18 was defensible because a real `<button>`, a focus
// ring, a `title` and `aria-sort` carry the control's identity; enumerate the
// same list here and every entry is hover-only or invisible — the `col-resize`
// cursor and the `title` both need the pointer already on the 9px strip, the
// focus ring needs a tab stop to have been reached, and `role="separator"` +
// `aria-valuenow` reach assistive technology and nobody else.
//
// 📏 Measured in Chromium against the background the cell REALLY has —
// `GAG_TABLE_HEADER_BG` is `bg-white dark:bg-gray-800` and
// `html.dark .bg-white { … #1e293b !important }` beats the non-important
// `dark:bg-gray-800`, so the two are **`#ffffff` and `#1e293b`, NOT the order
// table's `#f9fafb`**: rest **4.02:1 light / 3.91:1 dark**, hover and
// `focus-visible` **5.17 / 5.75**, identical across all 75 handles (15 columns
// × 5 groups).
//
// On the 2px × 16px grip: — and the full-height alternative was rendered side
// by side and reads unmistakably as a bordered header (this table draws **no
// vertical rules at all**: every `border-*-width` on its `<th>`/`<td>` measures
// `0px`).
//
// ⚠️ **The opacity-not-colour rule was re-derived for THIS property rather than
// inherited — the caret's cascade fact is about `color` and the answer differs
// for `background-color`**: `index.css`'s dark `!important` background table
// covers `.bg-white`, `.bg-gray-{50,100,200}`, the `-50/-100/-200` tints of
// eight hues and named `hover:bg-*` variants, and **nothing on
// `bg-gray-300`–`900` or `bg-blue-400/500/600`**, so a colour step on these
// shades is live in dark.
//
// 🪤 The blue moved `blue-400` → `blue-600` in light because `blue-400` measures
// **2.54:1 on white**, i.e. the old hover was FAINTER than the new resting
// state.
//
// On "the mark is not the hit area": the strip's `absolute right-0 top-0 h-full
// w-[9px] … justify-end` is unchanged, proven by re-running the
// `elementFromPoint` sweep with the old 1px transparent hairline forced back at
// runtime — byte-identical results in both themes.
